import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import "./ImpersonationBanner.css";
import { readAuth, endImpersonatedSession } from "../../services/authStorage";

/**
 * Shown while an operator is inside a member's account, opened from the CRM.
 *
 * Without it there is nothing on screen to say whose account this is: the app looks
 * exactly as it does to the member, which is the point of impersonating and also the
 * risk — support has already been one click away from writing as the customer. The
 * bar stays fixed at the top for the whole session and offers the only clean way out,
 * since until now the only exit was a full logout.
 */
const ImpersonationBanner = () => {
  const [isImpersonating, setIsImpersonating] = useState(
    () => readAuth("adoc_i") === "true",
  );

  const user = useSelector((state) => state.user?.user ?? state.user?.userInfo);

  // This mounts with the app, while /impersonate/:token sets the flag a moment later,
  // and a localStorage write raises no event in the tab that made it — so the page
  // announces the change itself. `storage` covers another tab, `focus` covers a
  // session restored while this one was in the background.
  useEffect(() => {
    const sync = () =>
      setIsImpersonating(readAuth("adoc_i") === "true");

    window.addEventListener("impersonation-changed", sync);
    window.addEventListener("storage", sync);
    window.addEventListener("focus", sync);

    return () => {
      window.removeEventListener("impersonation-changed", sync);
      window.removeEventListener("storage", sync);
      window.removeEventListener("focus", sync);
    };
  }, []);

  if (!isImpersonating) return null;

  const name =
    [user?.name, user?.surname].filter(Boolean).join(" ") ||
    user?.email ||
    "questo utente";

  const exit = () => {
    // Close only THIS tab's support session. The old code called
    // localStorage.clear(), which also signed the operator out of their own
    // account in every other tab of this browser — and wiped unrelated app
    // state with it. The impersonated session lives in sessionStorage now, so
    // there is nothing shared left to clear.
    endImpersonatedSession();
    window.close();
    // window.close() only works on tabs opened by script; when it doesn't, land
    // somewhere harmless rather than leaving the operator inside the account.
    window.location.href = "/login";
  };

  return (
    <div className="impersonation-banner">
      <span className="impersonation-banner__text">
        Stai vedendo l'app come <strong>{name}</strong>
      </span>
      <button type="button" className="impersonation-banner__exit" onClick={exit}>
        Esci
      </button>
    </div>
  );
};

export default ImpersonationBanner;
